import { ChangeDetectorRef, Component, ElementRef, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from 'src/app/components/forms/base-form/base-form.component';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { DialogService } from 'src/app/services/dialog.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { LanguageService } from 'src/app/services/language.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { PackagesService } from 'src/app/services/packages.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { LicenseAgreementDialogComponent } from 'src/app/components/utility/license-agreement-dialog/license-agreement-dialog.component';
import { LoggerService } from '../../services/logger.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent extends BaseComponent implements OnInit {

  // LanguageService is injected through the constructor (not an `inject()` field
  // initializer). BaseComponent's constructor triggers an async _csrfToken()
  // call which exits the synchronous injection context before subclass field
  // initializers run, so a field-level `inject()` here throws NG0203
  // ("inject() must be called from an injection context"). Constructor params,
  // forwarded to super() the same way every other BaseComponent subclass does
  // (see DayViewComponent), are bound before that side effect.
  constructor(
    _refreshService: RefreshService,
    _dialogService: DialogService,
    _entityRESTService: EntityRESTService,
    _utilityService: UtilityService,
    _fieldsService: FieldsService,
    _drupalRESTService: DrupalRESTService,
    _taxonomyService: TaxonomyService,
    _router: Router,
    _activatedRoute: ActivatedRoute,
    _authRESTService: AuthRESTService,
    _authService: AuthService,
    _menuRESTService: MenuRESTService,
    _formBuilder: FormBuilder,
    _routeEventsService: RouteEventsService,
    _permissionsService: PermissionsService,
    _renderer2: Renderer2,
    _configPages: ConfigPagesService,
    _packagesService: PackagesService,
    _snackBar: MatSnackBar,
    _userRESTService: UserService,
    _el: ElementRef,
    _changeDetectorRef: ChangeDetectorRef,
    _sanitizer: DomSanitizer,
    _studioConfigService: StudioConfigService,
    _componentRefreshService: ComponentRefreshService,
    _translateService: TranslateService,
    private _languageService: LanguageService,
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService,
    );
  }

  override onSubmit(f: NgForm) {
    let values = f.form.value;

    this.displayProgressSpinner(true);

    this._authRESTService.login(values.name, values.pass)
      .subscribe(data => {
        LoggerService.log('Login response data:', data);
        if (data.success === false) {
          this.errorMessage = data.message || 'Authentication failed, please try again.';
          this.displayProgressSpinner(false);
        } else {
          // Store authentication data
          this._authService.current_user = data?.['current_user'];
          this._authService.csrf_token = data['csrf_token'];
          this._authService.logout_token = data['logout_token'];

          // Apply the user's saved language from their Drupal account.
          this._languageService.applyUserLanguage();

          // Set user as authenticated first so API calls work
          this._authService.set_user_authenticated(true);

          // Initial studios from login response (may be limited)
          LoggerService.log('Setting initial studios from login:', data['studios']);
          if (data['studios'] && Array.isArray(data['studios']) && data['studios'].length > 0) {
            this._authService.studios = data['studios'];
          }

          // Load the complete studio list
          this.loadUserStudios(() => {
            // Get the menu after studios are loaded
            this._menuRESTService.getMenu();

            // Initialize other data
            this._fieldsService.getInstructorData();
            this._taxonomyService.initTaxonomyTerms();
            this._packagesService.fetchPackages();

            // Check if the user is a Franchisee and hasn't agreed to the license
            const userRoles = this._authService.current_user?.['roles'] || [];
            const isFranchisee = userRoles.includes('franchisee');

            if (isFranchisee && (!data['field_license_agreed'] || data['field_license_agreed'] === '0')) {
              this.openLicenseAgreementDialog();
            } else {
              // Navigate to dashboard
              this._router.navigate(['/app/splash']);
            }

            this.displayProgressSpinner(false);
          });
        }
      },
        error => this.handleError(error)
      );
  }

  /**
   * Load studios associated with the current user
   */
  loadUserStudios(callback?: Function): void {
    LoggerService.log('Login component loading studios list');
    this._drupalRESTService.httpGET('/api_rest/v1/loadStudioList').subscribe({
      next: (data: any) => {
        LoggerService.log('Login component received studios from API:', data);
        if (data && Array.isArray(data)) {
          // Update the auth service with the complete list of studios
          this._authService.studios = data;

          // If no studio is selected but we have studios available, select the first one
          if ((!this._authService.selectedStudio) && data.length > 0) {
            this._authService.selectedStudio = data[0];
          }
        } else {
          LoggerService.warn('Studio data is not an array or is empty:', data);
        }

        if (callback) {
          callback();
        }
      },
      error: (error) => {
        LoggerService.error('Error loading studios in login component:', error);
        if (callback) {
          callback();
        }
      }
    });
  }

  openLicenseAgreementDialog(): void {
    const dialogRef = this._dialogService.openDialog(LicenseAgreementDialogComponent, 'fullScreenCreditCardInput');

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // User agreed to the license
        const userId = this._authService.current_user?.['uid'];
        const updateData = {
          uid: userId,
          field_license_agreed: true,
          field_license_agreed_at: this.formatDateForMySQL(new Date()),
        };

        this._userRESTService.updateUser(userId, updateData).subscribe(
          response => {
            this._router.navigate(['/app/splash']);
          },
          error => {
            this.errorMessage = "Failed to update user license agreement: " + error.message;
          }
        );
      } else {
        localStorage.removeItem('csrf_token');
        this._authService.set_user_authenticated(false);
        this._router.navigate(['/auth/login'], {queryParams: {logged_out: true}})
        this._authService.current_user = null;
        this.successMessage = null;
        this.errorMessage = "You must agree to the license agreement to proceed.";
      }
    });
  }

  ngOnInit(): void {
    this._activatedRoute.queryParams
      .subscribe(params => {
        if (params['logged_out']) {
          this.successMessage = "You have been logged out.";
        }
        if (params['logged_out_inactive']) {
          this.successMessage = "You have been logged out due to inactivity.";
        }
      });
  }

  formatDateForMySQL(date: Date): string {
    return date.getFullYear() + '-' +
           ('00' + (date.getMonth() + 1)).slice(-2) + '-' +
           ('00' + date.getDate()).slice(-2) + 'T' +
           ('00' + date.getHours()).slice(-2) + ':' +
           ('00' + date.getMinutes()).slice(-2) + ':' +
           ('00' + date.getSeconds()).slice(-2);
  }
}
